import { MAX_UPLOAD_FILE_SIZE_BYTES, MAX_UPLOAD_FILE_SIZE_MB } from "@inrev/common";
import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type ReactNode, type Ref, useState } from "react";
import { type Accept, ErrorCode, type FileRejection, useDropzone } from "react-dropzone";
import { logDataToSentry } from "../../../utils/sentry";
import {
	getUploadErrorInlineMessage,
	isUploadErrorCode,
	UploadErrorCode,
} from "../../../utils/upload-error-messages";
import { cn } from "../../lib/utils";
import { Icon, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";

/**
 * The document types the platform accepts. Explicitly defined vs. derived via `getMimeTypes`,
 * which depends on `initializeMimeTypes()` having run at app bootstrap.
 */
export const DOCUMENT_ACCEPT: Accept = {
	"application/pdf": [".pdf"],
	"image/png": [".png"],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/gif": [".gif"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};

// Rejects zero-byte files, which upload cleanly but are never a usable document.
const MIN_FILE_SIZE_BYTES = 1;

// NOTE: The background and border colors (resting, hover, and drag-active) intentionally use
// native Tailwind grays instead of semantic tokens so this dropzone renders identically to the
// legacy `ui/Dropzone`. Keep the two in sync until both are migrated to semantic tokens together
// (`bc-default` maps to gray-200, not gray-300, so switching now would break the match).
// See apps/portal/src/components/ui/Dropzone.tsx.
/** Height of the `size="sm"` dropzone. Exported so hosts rendering a same-sized placeholder
 * (e.g. an upload-in-progress state) alongside the dropzone can't silently drift out of sync. */
export const DROPZONE_SM_HEIGHT_CLASS = "h-[46px]";

const boxVariants = cva(
	"w-full flex flex-col items-center justify-center gap-[10px] rounded-md border-[2px] border-dashed border-gray-300 bg-gray-50 bg-opacity-[.65] text-secondary select-none transition-colors",
	{
		variants: {
			size: {
				sm: DROPZONE_SM_HEIGHT_CLASS,
				md: "h-[110px]",
				lg: "h-[160px]",
			},
			interactive: {
				true: "cursor-pointer hover:bg-gray-100 hover:bg-opacity-60",
				false: "opacity-50 cursor-not-allowed",
			},
			dragActive: {
				true: "bg-gray-100 border-gray-500",
				false: "",
			},
		},
		defaultVariants: { size: "md", interactive: true, dragActive: false },
	},
);

const rejectionMessage = (code: string): string =>
	getUploadErrorInlineMessage(isUploadErrorCode(code) ? code : UploadErrorCode.unknown);

const logInvalidTypesToSentry = (rejections: FileRejection[]) => {
	const invalidTypes = rejections.flatMap((rejection) =>
		rejection.errors.some((error) => error.code === ErrorCode.FileInvalidType)
			? [{ fileName: rejection.file.name, mimeType: rejection.file.type }]
			: [],
	);
	if (invalidTypes.length > 0) logDataToSentry("Invalid file type", invalidTypes);
};

export type DropzoneProps = {
	onDrop: (files: File[]) => void;
	/** Restrict accepted MIME types / extensions (react-dropzone `Accept`). Omit to accept any file. */
	accept?: Accept;
	multiple?: boolean;
	disabled?: boolean;
	disabledMessage?: string;
	size?: VariantProps<typeof boxVariants>["size"];
	placeholder?: string;
	/** Caption under the drop target. Pass `null` to hide; defaults to the max-file-size hint. */
	caption?: ReactNode;
	className?: string;
	testId?: string;
	trackingId?: string;
	/**
	 * Fires the raw rejections alongside the inline caption. Invalid types are already logged to
	 * Sentry here — use this only for host-specific telemetry.
	 */
	onReject?: (rejections: FileRejection[]) => void;
	ref?: Ref<HTMLDivElement>;
};

export const Dropzone: FC<DropzoneProps> = ({
	onDrop,
	accept,
	multiple = false,
	disabled = false,
	disabledMessage = "File uploading is not allowed",
	size,
	placeholder = "Upload Documents",
	caption,
	className,
	testId,
	trackingId,
	onReject,
	ref,
}) => {
	const [rejection, setRejection] = useState<string | null>(null);

	const handleDrop = (accepted: File[], rejections: FileRejection[]) => {
		if (rejections.length > 0) {
			onReject?.(rejections);
			logInvalidTypesToSentry(rejections);
			const code = rejections[0]?.errors[0]?.code ?? "";
			setRejection(rejectionMessage(code));
			return;
		}
		setRejection(null);
		if (accepted.length > 0) onDrop(accepted);
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleDrop,
		maxSize: MAX_UPLOAD_FILE_SIZE_BYTES,
		minSize: MIN_FILE_SIZE_BYTES,
		accept,
		multiple,
		disabled,
	});

	const resolvedCaption =
		caption === undefined ? `Max file size: ${MAX_UPLOAD_FILE_SIZE_MB}MB` : caption;

	return (
		<div className="flex w-full flex-col gap-1.5" ref={ref} {...trackingAttrs(trackingId)}>
			<div
				{...getRootProps()}
				data-testid={testId}
				data-drag-active={isDragActive}
				className={cn(
					boxVariants({ size, interactive: !disabled, dragActive: isDragActive && !disabled }),
					className,
				)}
			>
				<input {...getInputProps()} className="hidden" />
				{disabled ? (
					<Text type="body" color="tertiary">
						{disabledMessage}
					</Text>
				) : (
					<span className="flex items-center gap-[10px]">
						<Icon type="document-plus" size="small" color="secondary" />
						<Text type="body" color="secondary">
							{placeholder}
						</Text>
					</span>
				)}
			</div>
			{!disabled && (rejection || resolvedCaption) && (
				<div className="flex min-h-[16px] items-start justify-between gap-3">
					{rejection ? (
						<Text type="caption" color="danger">
							{rejection}
						</Text>
					) : (
						<span />
					)}
					{resolvedCaption && (
						<Text type="caption" color="tertiary" className="ml-auto whitespace-nowrap">
							{resolvedCaption}
						</Text>
					)}
				</div>
			)}
		</div>
	);
};
Dropzone.displayName = "Dropzone";
